'use client';

import { createClient } from '@/lib/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';

export interface ProductPrice {
  id: string;
  productId: string;
  productName: string;
  productNameTa: string;
  currentPrice: number;
  previousPrice: number | null;
  priceChange: number;
  priceChangePct: number;
  trend: 'up' | 'down' | 'stable';
  unit: string;
  category: string;
  updatedAt: string;
}

function toProductPrice(row: any): ProductPrice {
  return {
    id: row.id,
    productId: row.product_id,
    productName: row.product_name,
    productNameTa: row.product_name_ta,
    currentPrice: Number(row.current_price),
    previousPrice: row.previous_price != null ? Number(row.previous_price) : null,
    priceChange: Number(row.price_change ?? 0),
    priceChangePct: Number(row.price_change_pct ?? 0),
    trend: row.trend ?? 'stable',
    unit: row.unit ?? 'kg',
    category: row.category ?? 'vegetables',
    updatedAt: row.updated_at,
  };
}

/** Fetch all current prices from Supabase */
export async function getAllProductPrices(): Promise<ProductPrice[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('product_prices')
    .select('*')
    .order('product_name', { ascending: true });

  if (error) {
    console.error('Failed to fetch product prices:', error.message);
    return [];
  }
  return (data || []).map(toProductPrice);
}

/** Fetch a single product's current price */
export async function getProductPrice(productId: string): Promise<ProductPrice | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('product_prices')
    .select('*')
    .eq('product_id', productId)
    .maybeSingle();

  if (error) {
    console.error('Failed to fetch product price:', error.message);
    return null;
  }
  return data ? toProductPrice(data) : null;
}

export interface PriceAlertMatch {
  alertId: string;
  productId: string;
  productName: string;
  productNameTa: string;
  condition: 'below' | 'above';
  threshold: number;
  currentPrice: number;
}

/**
 * Subscribe to real-time price changes on the product_prices table.
 * When a price UPDATE arrives, immediately checks all active user alerts
 * and calls onAlertTriggered for each threshold that has been crossed.
 *
 * @param userId - The authenticated user's ID (for filtering their alerts)
 * @param onPriceUpdate - Called with the updated price record
 * @param onAlertTriggered - Called instantly when a price crosses a threshold
 * @returns cleanup function to unsubscribe
 */
export function subscribeToProductPrices(
  userId: string,
  onPriceUpdate: (price: ProductPrice) => void,
  onAlertTriggered: (match: PriceAlertMatch) => void
): () => void {
  const supabase = createClient();

  const channel: RealtimeChannel = supabase
    .channel('product_prices_realtime')
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'product_prices',
      },
      async (payload) => {
        const updatedPrice = toProductPrice(payload.new);
        onPriceUpdate(updatedPrice);

        // Immediately check this user's active alerts for the updated product
        const { data: alerts, error } = await supabase
          .from('price_alerts')
          .select('*')
          .eq('user_id', userId)
          .eq('product_id', updatedPrice.productId)
          .eq('is_active', true);

        if (error || !alerts?.length) return;

        const now = Date.now();
        const sixHoursMs = 6 * 60 * 60 * 1000;

        for (const alert of alerts) {
          // Debounce: skip if triggered within last 6 hours
          if (alert.last_triggered_at) {
            const lastTriggered = new Date(alert.last_triggered_at).getTime();
            if (now - lastTriggered < sixHoursMs) continue;
          }

          const threshold = Number(alert.threshold);
          const shouldTrigger =
            (alert.condition === 'below' && updatedPrice.currentPrice < threshold) ||
            (alert.condition === 'above' && updatedPrice.currentPrice > threshold);

          if (!shouldTrigger) continue;

          // Fire the instant alert callback
          onAlertTriggered({
            alertId: alert.id,
            productId: updatedPrice.productId,
            productName: alert.product_name,
            productNameTa: alert.product_name_ta,
            condition: alert.condition,
            threshold,
            currentPrice: updatedPrice.currentPrice,
          });

          // Update last_triggered_at to prevent spam
          await supabase
            .from('price_alerts')
            .update({ last_triggered_at: new Date().toISOString() })
            .eq('id', alert.id);
        }
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'product_prices',
      },
      (payload) => {
        onPriceUpdate(toProductPrice(payload.new));
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * Subscribe to real-time changes for a single product's price.
 * Used on product detail pages.
 */
export function subscribeToSingleProductPrice(
  productId: string,
  onPriceUpdate: (price: ProductPrice) => void
): () => void {
  const supabase = createClient();

  const channel: RealtimeChannel = supabase
    .channel(`product_price_${productId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'product_prices',
        filter: `product_id=eq.${productId}`,
      },
      (payload) => {
        onPriceUpdate(toProductPrice(payload.new));
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
